import type { PwaCalendarBooking } from '@/calendar/pwaCalendarUtils';

/** Título de la card: siempre el recurso (cabaña / unidad), nunca "Reserved" / iCal summary. */
export function bookingCardTitle(booking: PwaCalendarBooking): string {
  const name = booking.resourceName?.trim();
  return name || 'Sin recurso';
}

function isOtaLike(booking: PwaCalendarBooking): boolean {
  return (
    booking.bookingOrigin === 'ota' ||
    booking.createdBy === 'ical_import' ||
    booking.externalSource === 'ical' ||
    booking.externalSource === 'ical_min_stay'
  );
}

/** Reserva confirmada en Airbnb (`Reserved`) frente a un bloqueo del calendario (`Not available`). */
export type AirbnbStayKind = 'reserved' | 'block';

/**
 * Distingue una estadía importada de Airbnb de un bloqueo del calendario.
 * Otros canales y las reservas de Interactive Talker devuelven null.
 */
export function airbnbStayKind(booking: PwaCalendarBooking): AirbnbStayKind | null {
  if (!isOtaLike(booking)) return null;
  if (booking.otaChannel && booking.otaChannel !== 'airbnb') return null;
  const lower = (booking.customerName ?? '').trim().toLowerCase();
  if (
    !lower ||
    lower.includes('not available') ||
    lower.includes('no disponible') ||
    lower.includes('unavailable') ||
    /\bbloqueo\b/.test(lower) ||
    /\bblocked\b/.test(lower)
  ) {
    return 'block';
  }
  return 'reserved';
}

/** Etiqueta corta para la grilla. Null si conviene mostrar el nombre real del huésped. */
export function airbnbStayShortLabel(booking: PwaCalendarBooking): string | null {
  const kind = airbnbStayKind(booking);
  if (kind === 'block') return 'Bloqueo';
  if (kind === 'reserved' && isIcalMachineLabel(booking)) return 'Reservado';
  return null;
}

/** True when customerName is an OTA/iCal machine label, not a real guest. */
export function isIcalMachineLabel(booking: PwaCalendarBooking): boolean {
  if (!isOtaLike(booking)) return false;
  const lower = (booking.customerName ?? '').trim().toLowerCase();
  if (!lower) return true;
  return (
    lower.includes('not available') ||
    lower.includes('no disponible') ||
    lower.includes('unavailable') ||
    lower === 'reserved' ||
    lower.startsWith('reserved') ||
    /\bbloqueo\b/.test(lower) ||
    /\bblocked\b/.test(lower) ||
    /^airbnb\b/.test(lower)
  );
}

/**
 * Texto entendible del “estado” iCal (Reserved / Not available) para el badge.
 * Ej.: "Reserved" → "Reservado en Airbnb".
 */
export function bookingOccupancyLabel(booking: PwaCalendarBooking): string {
  const raw = (booking.customerName ?? '').trim();
  const lower = raw.toLowerCase();

  if (isOtaLike(booking)) {
    if (
      lower.includes('not available') ||
      lower.includes('no disponible') ||
      lower.includes('unavailable') ||
      /\bbloqueo\b/.test(lower) ||
      /\bblocked\b/.test(lower)
    ) {
      return 'Bloqueo / no disponible en Airbnb';
    }
    if (lower === 'reserved' || lower.startsWith('reserved')) {
      return 'Reservado en Airbnb';
    }
    if (raw) return raw;
    return 'Bloqueo Airbnb';
  }

  if (raw) return raw;
  return 'Huésped';
}

/** Badge derecha: descripción iCal si aplica; si no, estado de la reserva (Confirmada, etc.). */
export function bookingCardStatusLabel(
  booking: PwaCalendarBooking,
  workflowStatusLabel: (status: string) => string
): string {
  if (isOtaLike(booking) && isIcalMachineLabel(booking)) {
    return bookingOccupancyLabel(booking);
  }
  return workflowStatusLabel(booking.status);
}

/** Día de calendario UTC (YYYY-MM-DD) → DD/MM/YYYY. */
function formatUtcDayLabel(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = d.getUTCFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Fechas ini/fin visibles para el operador (días UTC del booking diario).
 * Ej.: "Ini 02/08/2026 · Fin 05/08/2026"
 */
export function formatBookingDateRange(startIso: string, endIso: string): string {
  const a = formatUtcDayLabel(startIso);
  const b = formatUtcDayLabel(endIso);
  if (!a && !b) return 'Sin fechas';
  if (a && !b) return `Ini ${a}`;
  if (!a && b) return `Fin ${b}`;
  if (a === b) return `Ini ${a}`;
  return `Ini ${a} · Fin ${b}`;
}

export function truncateId(id: string, max = 12): string {
  const t = id.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max)}…`;
}

export function isOtaBooking(booking: PwaCalendarBooking): boolean {
  return isOtaLike(booking);
}
